import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Search, User, LogOut, Building2, FolderOpen, CheckSquare, Menu, PanelLeftOpen, ArrowLeft, Moon, Sun,
  ReceiptEuro, Target, Car, FileText, ChevronRight, UserCircle, Users, FolderKanban, Globe, BookText, Calendar, History, Tag, Radar, Mail, FileSignature, Wrench
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { datumDe } from '../../utils/wartung';
import { useTabs } from '../../contexts/TabContext';
import { useAufgabenFenster } from '../../contexts/AufgabenFensterContext';
import { useTerminFenster } from '../../contexts/TerminFensterContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dashboardService from '../../services/dashboardService';
import recentlyVisitedService from '../../services/recentlyVisitedService';
import api from '../../services/api';
import { useMobileSidebar } from './Layout';
import { NotificationBell } from './NotificationBell';
import { UpdateChecker } from './UpdateChecker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '../ui/context-menu';
import useNotizbuch from '../../hooks/useNotizbuch';
import { useStudentDesignWert, setzeStudentDesign } from '../../hooks/useStudentDesign';

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { oeffnen: aufgabenFensterOeffnen } = useAufgabenFenster();
  const { oeffnen: terminFensterOeffnen } = useTerminFenster();
  const openNotizbuch = useNotizbuch();
  const { openTab } = useTabs();
  const location = useLocation();
  const { i18n } = useTranslation();

  const LANGUAGES = [
    { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
    { code: 'en', flag: '🇬🇧', label: 'English' },
    { code: 'ru', flag: '🇷🇺', label: 'Русский' },
  ];
  const currentLang = LANGUAGES.find((l) => i18n.language?.startsWith(l.code)) || LANGUAGES[0];
  const changeLanguage = (code) => i18n.changeLanguage(code);
  const toggleLanguage = () => {
    const newLang = i18n.language === 'de' ? 'en' : 'de';
    i18n.changeLanguage(newLang);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const [recentAddresses, setRecentAddresses] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);

  const loadRecentItems = useCallback(() => {
    const all = recentlyVisitedService.getAll();
    setRecentAddresses(all.filter(i => i.type === 'address').slice(0, 5));
    setRecentProjects(all.filter(i => i.type === 'project').slice(0, 5));
  }, []);

  useEffect(() => {
    loadRecentItems();
    window.addEventListener('recentItemsUpdated', loadRecentItems);
    window.addEventListener('recentProjectsUpdated', loadRecentItems);
    return () => {
      window.removeEventListener('recentItemsUpdated', loadRecentItems);
      window.removeEventListener('recentProjectsUpdated', loadRecentItems);
    };
  }, [loadRecentItems]);
  
  // Mobile sidebar control
  const mobileSidebar = useMobileSidebar();

  // Family-Mobile: Globale Suche ausblenden
  const isFamily = user?.tenant_type === 'family';
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const hideGlobalSearch = isFamily && isMobile;

  // EasyWork Student, Handy: Zurück-Pfeil links oben (wie in Shopping-Apps) auf allen Seiten außer Home.
  // Ohne vorherige Seite in der App-Historie (z. B. Direktaufruf per Link) geht es zur Startseite.
  const isStudent = user?.role === 'student' || !!user?.is_student;
  const zeigeZurueck = isStudent && isMobile && location.pathname !== '/dashboard';
  const studentDesign = useStudentDesignWert();
  const zurueck = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/dashboard');
  };

  // Close search dropdown when route changes
  useEffect(() => {
    setShowResults(false);
    setSearchQuery('');
    setSearchResults(null);
  }, [location.pathname]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle Escape key to close dropdown
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowResults(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchQuery.length >= 2) {
      setSearching(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const results = await dashboardService.search(searchQuery);
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setSearching(false);
        }
      }, 300);
    } else {
      setSearchResults(null);
      setShowResults(false);
      setSearching(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleResultClick = (type, id, entityType = null, entityId = null) => {
    setShowResults(false);
    setSearchQuery('');
    
    switch (type) {
      case 'address':
        navigate(`/addresses/${id}`);
        break;
      case 'contact_person':
        // Navigate to parent address with contact persons tab
        navigate(`/addresses/${entityType}?tab=kontaktpersonen`);
        break;
      case 'project':
        navigate(`/projects/${id}`);
        break;
      case 'task':
        // Suchtreffer öffnen das Aufgabenfenster direkt
        aufgabenFensterOeffnen({ taskId: id });
        break;
      case 'order':
        navigate(`/invoices/edit/${id}`);
        break;
      case 'lead':
        navigate(`/leads/${id}`);
        break;
      case 'fleet':
        navigate(`/fleet?vehicle=${id}`);
        break;
      case 'document':
        // Navigate to the entity the document belongs to
        if (entityType === 'address') {
          navigate(`/addresses/${entityId}?tab=documents`);
        } else if (entityType === 'project') {
          navigate(`/projects/${entityId}?tab=documents`);
        } else {
          navigate(`/documents?highlight=${id}`);
        }
        break;
      default:
        break;
    }
  };

  const handleShowAllResults = () => {
    setShowResults(false);
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const getAddressDisplayName = (addr) => {
    if (addr.type === 'company' && addr.company_name) {
      return addr.company_name;
    }
    return `${addr.first_name || ''} ${addr.last_name || ''}`.trim() || addr.email || 'Unbekannt';
  };

  const totalResults = searchResults?.total_count || 0;

  // Category configuration
  const categories = [
    {
      key: 'addresses',
      label: 'Adressen',
      icon: Building2,
      iconColor: 'text-blue-500',
      getTitle: (item) => getAddressDisplayName(item),
      getSubtitle: (item) => item.city || item.email,
      getBadge: (item) => item.customer_number,
    },
    {
      key: 'address_categories',
      label: 'Adresskategorien',
      icon: Tag,
      iconColor: 'text-amber-500',
      getTitle: (item) => item.name,
      getSubtitle: () => 'Adressliste nach dieser Kategorie filtern',
      onClick: (item) => {
        try { localStorage.setItem('addr_categoryFilter', JSON.stringify([item.name])); } catch { /* Storage evtl. deaktiviert */ }
        setShowResults(false);
        setSearchQuery('');
        navigate('/addresses');
      },
    },
    {
      key: 'contact_persons',
      label: 'Ansprechpartner',
      icon: UserCircle,
      iconColor: 'text-teal-500',
      getTitle: (item) => `${item.first_name || ''} ${item.last_name || ''}`.trim(),
      getSubtitle: (item) => item.parent_address_name || item.position,
      getBadge: (item) => item.position,
      onClick: (item) => handleResultClick('contact_person', item.id, item.parent_address_id),
    },
    {
      key: 'projects',
      label: 'Projekte',
      icon: FolderOpen,
      iconColor: 'text-green-500',
      getTitle: (item) => item.name,
      getSubtitle: (item) => item.project_number,
      getBadge: (item) => item.status,
      getBadgeClass: (item) => 
        item.status === 'active' ? 'bg-blue-100 text-blue-700' :
        item.status === 'completed' ? 'bg-green-100 text-green-700' :
        'bg-gray-100 text-gray-700',
    },
    {
      key: 'tasks',
      label: 'Aufgaben',
      icon: CheckSquare,
      iconColor: 'text-orange-500',
      getTitle: (item) => item.title,
      getSubtitle: (item) => item.due_date ? `Fällig: ${new Date(item.due_date).toLocaleDateString('de-DE')}` : null,
      getBadge: (item) => item.priority,
      getBadgeClass: (item) => 
        item.priority === 'urgent' ? 'bg-red-100 text-red-700' :
        item.priority === 'high' ? 'bg-orange-100 text-orange-700' :
        'bg-gray-100 text-gray-700',
    },
    {
      key: 'orders',
      label: 'Aufträge',
      icon: ReceiptEuro,
      iconColor: 'text-purple-500',
      getTitle: (item) => item.title || item.order_number,
      getSubtitle: (item) => item.customer_name,
      getBadge: (item) => item.status,
      getBadgeClass: () => 'bg-purple-100 text-purple-700',
    },
    {
      key: 'leads',
      label: 'Leads',
      icon: Target,
      iconColor: 'text-pink-500',
      getTitle: (item) => item.name || item.company,
      getSubtitle: (item) => item.company || item.email,
      getBadge: (item) => item.status,
      getBadgeClass: (item) => 
        item.status === 'hot' ? 'bg-red-100 text-red-700' :
        item.status === 'warm' ? 'bg-orange-100 text-orange-700' :
        'bg-blue-100 text-blue-700',
    },
    {
      key: 'fleet',
      label: 'Fuhrpark',
      icon: Car,
      iconColor: 'text-cyan-500',
      getTitle: (item) => item.license_plate,
      getSubtitle: (item) => `${item.brand || ''} ${item.model || ''}`.trim(),
      getBadge: (item) => item.status,
      getBadgeClass: (item) => 
        item.status === 'active' ? 'bg-green-100 text-green-700' :
        'bg-gray-100 text-gray-700',
    },
    {
      key: 'contracts',
      label: 'Verträge',
      icon: FileSignature,
      iconColor: 'text-teal-600',
      getTitle: (item) => item.description,
      getSubtitle: (item) => [item.partner, item.contract_number && `Nr. ${item.contract_number}`].filter(Boolean).join(' · '),
      getBadge: (item) => (item.contract_type === 'wartung' ? 'Wartung' : null),
      getBadgeClass: () => 'bg-teal-100 text-teal-700',
      onClick: (item) => {
        setShowResults(false);
        setSearchQuery('');
        navigate(`/contracts?vertrag=${item.id}`);
      },
    },
    {
      key: 'anlagen',
      label: 'Anlagen',
      icon: Wrench,
      iconColor: 'text-teal-600',
      getTitle: (item) => item.name,
      getSubtitle: (item) => [item.standort, item.faellig_am && `nächste Wartung ${datumDe(item.faellig_am)}`].filter(Boolean).join(' · '),
      onClick: (item) => {
        setShowResults(false);
        setSearchQuery('');
        navigate(item.project_id ? `/projects/${item.project_id}?tab=wartung` : '/contracts');
      },
    },
    {
      key: 'documents',
      label: 'Dokumente',
      icon: FileText,
      iconColor: 'text-amber-500',
      getTitle: (item) => item.original_filename || item.filename,
      getSubtitle: (item) => item.context,
      onClick: (item) => handleResultClick('document', item.id, item.entity_type, item.entity_id),
    },
    {
      key: 'notes',
      label: 'Notizen',
      icon: BookText,
      iconColor: 'text-yellow-500',
      getTitle: (item) => item.title || '(Ohne Titel)',
      getSubtitle: (item) => item.snippet || '',
      getBadge: (item) => (item.tags || [])[0],
      getBadgeClass: () => 'bg-yellow-100 text-yellow-700',
      onClick: (item) => {
        setShowResults(false);
        setSearchQuery('');
        // Notiz im Notizbuch-Fenster öffnen
        openNotizbuch({ noteId: item.id });
      },
    },
    {
      key: 'appointments',
      label: 'Termine',
      icon: Calendar,
      iconColor: 'text-purple-500',
      getTitle: (item) => item.title || '(Ohne Titel)',
      getSubtitle: (item) => {
        if (!item.start_time) return item.location || '';
        try {
          const d = new Date(item.start_time);
          const datePart = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const timePart = item.all_day ? 'Ganztägig' : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
          const loc = item.location ? ` · ${item.location}` : '';
          return `${datePart}, ${timePart}${loc}`;
        } catch {
          return item.location || '';
        }
      },
      onClick: (item) => {
        setShowResults(false);
        setSearchQuery('');
        // Suchtreffer öffnen das Terminfenster direkt
        terminFensterOeffnen({ terminId: item.id });
      },
    },
    {
      key: 'historie',
      label: 'Historie',
      icon: History,
      iconColor: 'text-slate-500',
      getTitle: (item) => item.summary || '(Eintrag)',
      getSubtitle: (item) => {
        const parts = [];
        if (item.entity) parts.push(item.entity);
        if (item.action) parts.push(item.action);
        if (item.user_email) parts.push(item.user_email);
        if (item.at) {
          try { parts.push(new Date(item.at).toLocaleString('de-DE')); } catch {}
        }
        return parts.join(' · ');
      },
      onClick: () => {
        setShowResults(false);
        setSearchQuery('');
        navigate('/gastro?tab=historie');
      },
    },
    {
      key: 'market_radar',
      label: 'MarktRadar',
      icon: Radar,
      iconColor: 'text-fuchsia-500',
      getTitle: (item) => item.title,
      getSubtitle: (item) => (
        item.kind === 'segment' ? 'Zielgruppensegment' : item.kind === 'persona' ? 'KI-generierte Persona' : 'MarktRadar-Empfehlung'
      ),
      onClick: () => {
        setShowResults(false);
        setSearchQuery('');
        navigate('/marketing?tab=markt&ansicht=radar');
      },
    },
  ];

  return (
    <header className="flex h-14 sm:h-16 items-center justify-between border-b bg-[var(--ew-header-bg)] px-3 sm:px-6">
      {/* Mobile Menu Button + Search */}
      <div className="flex flex-1 items-center gap-2 sm:gap-4">
        {zeigeZurueck && (
          <button
            type="button"
            onClick={zurueck}
            className="md:hidden inline-flex items-center justify-center -ml-1 h-11 w-11 shrink-0 rounded-full hover:bg-gray-100 active:bg-gray-200 text-gray-800"
            aria-label="Zurück"
            title="Zurück"
            data-testid="mobile-zurueck-btn"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
        )}
        {/* Hamburger Menu - Tablet only (md<=w<lg). Mobile uses BottomTabBar. */}
        <button
          onClick={mobileSidebar?.toggleMobileSidebar}
          className="hidden md:inline-flex lg:hidden p-2 -ml-1 rounded-lg hover:bg-gray-100 text-gray-600"
          aria-label="Menü öffnen"
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-5 w-5" />
        </button>
        {/* Eingeklappte Hauptnavigation wieder einblenden - Desktop (ab lg); eingeklappt wird im Kopf der Navigation */}
        {mobileSidebar?.isNavEingeklappt && (
          <button
            onClick={mobileSidebar.toggleNav}
            className="hidden lg:inline-flex p-2 -ml-1 rounded-lg hover:bg-gray-100 text-gray-600"
            aria-label="Navigation einblenden"
            title="Navigation einblenden"
            data-testid="hauptnav-umschalten"
          >
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        )}
        
        {/* Search - responsive width */}
        {!hideGlobalSearch && (
        <div className="relative flex-1 max-w-xs sm:max-w-sm lg:max-w-md xl:max-w-lg" ref={searchRef}>
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchResults && setShowResults(true)}
                  placeholder="Globale Suche... (min. 2 Zeichen)"
                  data-testid="global-search"
                  className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </ContextMenuTrigger>
            {/* Breit genug für lange Namen; Namen brechen um statt abgeschnitten zu werden */}
            <ContextMenuContent className="w-[30rem] max-w-[calc(100vw-2rem)]" data-testid="global-search-recent">
              {recentAddresses.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">Zuletzt geöffnete Adressen</div>
                  {recentAddresses.map((a, i) => (
                    <ContextMenuItem key={`addr-${i}`} onClick={() => navigate(a.link)} title={a.name}>
                      <Users className="w-4 h-4 mr-2 flex-shrink-0 text-blue-500" />
                      <span className="min-w-0 break-words">{a.name}</span>
                      {a.meta && <span className="ml-auto text-xs text-gray-400 pl-2 flex-shrink-0">{a.meta}</span>}
                    </ContextMenuItem>
                  ))}
                </>
              )}
              {recentAddresses.length > 0 && recentProjects.length > 0 && <ContextMenuSeparator />}
              {recentProjects.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">Zuletzt geöffnete Projekte</div>
                  {recentProjects.map((p, i) => (
                    <ContextMenuItem key={`proj-${i}`} onClick={() => navigate(p.link)} title={p.meta ? `${p.name} (${p.meta})` : p.name}>
                      <FolderKanban className="w-4 h-4 mr-2 flex-shrink-0 text-amber-500" />
                      <span className="min-w-0 break-words">{p.name}</span>
                      {p.meta && <span className="ml-auto text-xs text-gray-400 pl-2 flex-shrink-0">{p.meta}</span>}
                    </ContextMenuItem>
                  ))}
                </>
              )}
              {recentAddresses.length === 0 && recentProjects.length === 0 && (
                <ContextMenuItem disabled>Keine zuletzt geöffneten Einträge</ContextMenuItem>
              )}
            </ContextMenuContent>
          </ContextMenu>

          {/* Search Results Dropdown */}
          {showResults && (
            <div 
              className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg border border-gray-200 max-h-[70vh] overflow-y-auto z-50"
              style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.15)', borderRadius: '8px' }}
            >
              {searching ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    Suche läuft...
                  </div>
                </div>
              ) : totalResults === 0 ? (
                <div className="p-4">
                  <div className="text-center py-4" data-testid="global-search-empty">
                    <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Keine Ergebnisse für "{searchQuery}"</p>
                    <p className="text-gray-400 text-xs mt-1">Versuche andere Suchbegriffe</p>
                  </div>
                  {/* Internet-Fallback für Adressen: max. 5 Kandidaten aus öffentlicher Recherche */}
                  {searchQuery.trim().length >= 3 && (
                    <ExternalAddressFallback
                      key={searchQuery}
                      query={searchQuery.trim()}
                      onSelect={(candidate, sourceUrl) => {
                        setShowResults(false);
                        setSearchQuery('');
                        navigate('/addresses', {
                          state: {
                            openNew: true,
                            prefilled: candidateToAddressForm(candidate),
                            sourceUrl: sourceUrl || candidate.quelle_url || '',
                          },
                        });
                      }}
                    />
                  )}
                </div>
              ) : (
                <div>
                  {/* Internet-Fallback für Adressen: erscheint auch wenn Suche nur andere Kategorien traf */}
                  {(searchResults?.addresses?.length || 0) === 0 && searchQuery.trim().length >= 3 && (
                    <div className="p-4 border-b border-gray-100 bg-blue-50/40">
                      <ExternalAddressFallback
                        key={searchQuery}
                        query={searchQuery.trim()}
                        onSelect={(candidate, sourceUrl) => {
                          setShowResults(false);
                          setSearchQuery('');
                          navigate('/addresses', {
                            state: {
                              openNew: true,
                              prefilled: candidateToAddressForm(candidate),
                              sourceUrl: sourceUrl || candidate.quelle_url || '',
                            },
                          });
                        }}
                      />
                    </div>
                  )}
                  {categories.map((category) => {
                    const items = searchResults[category.key] || [];
                    if (items.length === 0) return null;

                    const Icon = category.icon;

                    return (
                      <div key={category.key}>
                        {/* Category Header */}
                        <div
                          className="px-4 py-2 text-xs font-bold uppercase tracking-wide flex items-center justify-between"
                          style={{ backgroundColor: 'var(--ew-nav-bg)', color: 'white' }}
                        >
                          <span className="flex items-center gap-2">
                            <Icon className="h-3.5 w-3.5" />
                            {category.label}
                          </span>
                          <span className="bg-white/20 px-2 py-0.5 rounded text-[10px]">
                            {items.length}
                          </span>
                        </div>
                        
                        {/* Category Items */}
                        {items.map((item) => {
                          // Map category key to the correct type for handleResultClick
                          const getTypeFromCategory = (categoryKey) => {
                            const typeMap = {
                              'addresses': 'address',
                              'contact_persons': 'contact_person',
                              'projects': 'project',
                              'tasks': 'task',
                              'orders': 'order',
                              'leads': 'lead',
                              'fleet': 'fleet',
                              'documents': 'document'
                            };
                            return typeMap[categoryKey] || categoryKey.replace(/s$/, '');
                          };
                          
                          return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (category.onClick) {
                                category.onClick(item);
                              } else {
                                handleResultClick(getTypeFromCategory(category.key), item.id);
                              }
                            }}
                            className="w-full px-4 py-3 flex items-center gap-3 text-left border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-100 cursor-pointer"
                            data-testid={`search-result-${category.key}-${item.id}`}
                          >
                            <Icon className={`h-4 w-4 ${category.iconColor} flex-shrink-0`} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate text-sm">
                                {category.getTitle(item)}
                              </p>
                              {category.getSubtitle(item) && (
                                <p className="text-xs text-gray-500 truncate">
                                  {category.getSubtitle(item)}
                                </p>
                              )}
                            </div>
                            {category.getBadge && category.getBadge(item) && (
                              <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${
                                category.getBadgeClass ? category.getBadgeClass(item) : 'bg-gray-100 text-gray-700'
                              }`}>
                                {category.getBadge(item)}
                              </span>
                            )}
                            <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                          </button>
                        );
                        })}
                      </div>
                    );
                  })}

                  {/* Show All Results Link */}
                  <button
                    onClick={handleShowAllResults}
                    className="w-full px-4 py-3 text-center text-sm font-medium text-blue-600 hover:bg-blue-50 border-t border-gray-200 flex items-center justify-center gap-2"
                  >
                    Alle {totalResults} Ergebnisse anzeigen
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        )}
        {/* Schnellzugriff: Kalender + E-Mail rechts neben der globalen Suche */}
        <div className="hidden sm:flex items-center gap-1 ml-2 shrink-0" data-testid="header-quick-access">
          <button
            type="button"
            onClick={() => { openTab('/calendar', 'Kalender', 'Calendar', 'calendar'); navigate('/calendar'); }}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-[#1E3A5F]"
            title="Kalender öffnen"
            data-testid="header-quick-calendar"
          >
            <Calendar className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => { openTab('/emails', 'E-Mails', 'Mail', 'emails'); navigate('/emails'); }}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-[#1E3A5F]"
            title="E-Mails öffnen"
            data-testid="header-quick-email"
          >
            <Mail className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center space-x-2 sm:space-x-4">
        {/* Sprach-Umschalter (Flaggen) — DE / EN / RU */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
              title="Sprache / Language / Язык"
              data-testid="language-switcher"
            >
              <span className="text-lg leading-none">{currentLang.flag}</span>
              <span className="hidden sm:inline text-xs font-semibold text-gray-600 uppercase">{currentLang.code}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {LANGUAGES.map((l) => (
              <DropdownMenuItem
                key={l.code}
                onClick={() => changeLanguage(l.code)}
                className={i18n.language?.startsWith(l.code) ? 'bg-blue-50 font-medium' : ''}
                data-testid={`language-option-${l.code}`}
              >
                <span className="mr-2 text-lg leading-none">{l.flag}</span>
                <span>{l.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Update Checker Badge - nur für Admins */}
        <UpdateChecker />
        
        {/* Notifications */}
        <NotificationBell />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className="flex items-center space-x-2 sm:space-x-3 rounded-lg p-1.5 sm:p-2 hover:bg-gray-100"
              data-testid="user-menu-btn"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white text-sm">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              {/* Hide name on small screens */}
              <div className="text-left hidden sm:block">
                <p className="text-sm font-medium text-gray-700">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Mein Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <User className="mr-2 h-4 w-4" />
              <span>Profil</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={toggleLanguage}>
              <Globe className="mr-2 h-4 w-4" />
              <span>{i18n.language === 'de' ? 'English' : 'Deutsch'}</span>
            </DropdownMenuItem>
            {/* EasyWork Student: auf dem Handy standardmäßig schwarz, hier umschaltbar */}
            {isStudent && (
              <DropdownMenuItem onClick={() => setzeStudentDesign(studentDesign === 'hell' ? 'dunkel' : 'hell')} data-testid="student-design-umschalten">
                {studentDesign === 'hell' ? <Moon className="mr-2 h-4 w-4" /> : <Sun className="mr-2 h-4 w-4" />}
                <span>{studentDesign === 'hell' ? 'Dunkles Design (Handy)' : 'Helles Design (Handy)'}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Abmelden</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

// === Internet-Fallback für die globale Adress-Suche ===
// Zeigt den „Internet-Suche starten"-Button, ruft `/api/addresses/search-external` erst
// bei explizitem Klick auf (Kostenkontrolle) und stellt max. 5 Kandidaten als Kurzkarten dar.
function ExternalAddressFallback({ query, onSelect }) {
  const [loading, setLoading] = React.useState(false);
  const [results, setResults] = React.useState(null); // null=noch nicht gesucht, []=keine Treffer
  const [error, setError] = React.useState('');

  const runSearch = async () => {
    setLoading(true); setError(''); setResults(null);
    try {
      const res = await api.get('/addresses/search-external', { params: { query } });
      setResults(Array.isArray(res.data?.results) ? res.data.results : []);
    } catch (e) {
      setError('Externe Recherche konnte nicht ausgeführt werden.');
      setResults([]);
    }
    setLoading(false);
  };

  if (results === null && !loading) {
    return (
      <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2.5 flex items-center gap-3" data-testid="external-search-prompt">
        <Search className="h-4 w-4 text-blue-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-800 truncate">Keine Adresse in EasyWork gefunden.</p>
          <p className="text-[11px] text-gray-500 truncate">Im Internet nach „{query}“ suchen?</p>
        </div>
        <button
          type="button"
          onClick={runSearch}
          className="text-xs font-semibold px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors whitespace-nowrap"
          data-testid="external-search-start"
        >
          Internet-Suche starten
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-500" data-testid="external-search-loading">
        <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        Suche läuft...
      </div>
    );
  }

  if (results && results.length === 0) {
    return (
      <div className="px-3 py-3 text-center text-xs text-gray-500" data-testid="external-search-empty">
        {error || 'Keine Treffer im Internet gefunden.'}
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="external-search-results">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1 flex items-center gap-1.5">
        <Globe className="h-3 w-3" /> Internet-Treffer ({results.length}) — Klick zum Übernehmen
      </div>
      {results.map((r, i) => (
        <button
          key={`${r.firma}-${i}`}
          type="button"
          onClick={() => onSelect(r, r.quelle_url)}
          className="w-full text-left rounded-md border border-gray-200 bg-white hover:border-blue-400 hover:bg-blue-50 transition-colors p-2.5"
          data-testid={`external-search-candidate-${i}`}
        >
          <div className="flex items-start gap-2">
            <Building2 className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900 truncate">{r.firma || '(ohne Namen)'}</div>
              <div className="text-[11px] text-gray-600 truncate">
                {[r.strasse, [r.plz, r.stadt].filter(Boolean).join(' '), r.land].filter(Boolean).join(' · ')}
              </div>
              {r.website && (
                <div className="text-[11px] text-blue-600 truncate">{r.website}</div>
              )}
              {r.branche && (
                <span className="inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {r.branche}
                </span>
              )}
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
          </div>
        </button>
      ))}
    </div>
  );
}

// Wandelt einen externen Kandidaten in das Adress-Formular-Schema um.
// Wichtig: Straße + Hausnummer sind im Formular separate Felder → wir splitten.
// Telefon wird ins internationale Format (+49 …) normalisiert, wenn möglich.
function candidateToAddressForm(c) {
  const strasse = (c.strasse || '').trim();
  let street = strasse;
  let street_number = '';
  const m = strasse.match(/^(.+?)\s+([0-9][0-9A-Za-z\-\/\s]*)$/);
  if (m) {
    street = m[1].trim();
    street_number = m[2].trim();
  }
  const normalizePhone = (raw) => {
    const s = (raw || '').trim();
    if (!s) return '';
    if (s.startsWith('+')) return s;
    if (s.startsWith('00')) return '+' + s.slice(2);
    if (s.startsWith('0')) return '+49 ' + s.slice(1).replace(/^\s+/, '');
    return s;
  };
  return {
    type: 'company',
    company_name: c.firma || '',
    website: c.website || '',
    phone: normalizePhone(c.telefon),
    street,
    street_number,
    postal_code: c.plz || '',
    city: c.stadt || '',
    country: c.land || 'Deutschland',
    address_type: 'Büroadresse',
    industry: c.branche ? [c.branche] : [],
    category: ['Interessent'],
  };
}
